import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { api, type PanelUser } from './api'

interface AuthContextValue {
  user: PanelUser | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const Ctx = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PanelUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const t = localStorage.getItem('beebro.token')
    if (!t) { setLoading(false); return }
    api.get<PanelUser>('/auth/me')
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem('beebro.token'))
      .finally(() => setLoading(false))
  }, [])

  const login = async (email: string, password: string) => {
    const r = await api.post<{ token: string; user: PanelUser }>('/auth/login', { email, password })
    localStorage.setItem('beebro.token', r.data.token)
    setUser(r.data.user)
  }

  const logout = async () => {
    try { await api.post('/auth/logout') } catch {}
    localStorage.removeItem('beebro.token')
    setUser(null)
    window.location.href = '/login'
  }

  return <Ctx.Provider value={{ user, loading, login, logout }}>{children}</Ctx.Provider>
}

export function useAuth() {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth outside AuthProvider')
  return v
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  const loc = useLocation()
  if (loading) return <div className="p-8 text-neutral-500">Loading…</div>
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />
  return <>{children}</>
}
